import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  FormControlLabel,
  FormControl,
  Switch,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { aiEventTypeApi, aiScheduleApi, alertApi } from '../../services/api';
import { ExpertMultiAutocomplete } from '../common/AsyncEntitySelect';
import { useAuth } from '../../context/AuthContext';
import {
  EventFilterFields,
  EventTypeAutocomplete,
  buildEventFilters,
  createEmptyEventFilterValue,
  eventTypeAccessErrorMessage,
  eventFilterValueFromFilters,
  getAccessibleEventTypes,
  type EventFilterFormValue,
} from '../schedule/EventTypeAutocomplete';
import type { AISchedule, AIScheduleCreate, ScheduleType, RecurringUnit, WeekDay } from '../../types';

interface SchedulePanelProps {
  systemId: number;
}

interface ScheduleFormData {
  type: ScheduleType;
  start_at: string;
  end_at: string | null;
  interval_period: RecurringUnit | null;
  interval_every: number | null;
  excluded_days: WeekDay[] | null;
  excluded_time_ranges: { start: string; end: string }[] | null;
  event_type: string;
  event_filters: EventFilterFormValue;
  event_cooldown_seconds: number;
  event_dedupe_window_seconds: number;
  event_while_running_policy: 'drop' | 'queue';
  prevent_concurrent_executions: boolean;
  concurrency_policy: 'queue' | 'drop';
  expert_selection_mode: 'ALL' | 'INCLUDE' | 'EXCLUDE';
  expert_ids: number[];
  is_active: boolean;
}

export const SchedulePanel: React.FC<SchedulePanelProps> = ({ systemId }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.is_staff === true || user?.is_superuser === true;
  const [formOpen, setFormOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<AISchedule | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<AISchedule | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const [formData, setFormData] = useState<ScheduleFormData>({
    type: 'ONE_TIME',
    start_at: '',
    end_at: null,
    interval_period: null,
    interval_every: null,
    excluded_days: null,
    excluded_time_ranges: null,
    event_type: '',
    event_filters: createEmptyEventFilterValue(),
    event_cooldown_seconds: 0,
    event_dedupe_window_seconds: 60,
    event_while_running_policy: 'drop',
    prevent_concurrent_executions: false,
    concurrency_policy: 'queue',
    expert_selection_mode: 'ALL',
    expert_ids: [],
    is_active: true,
  });

  // Fetch schedules for this AI system
  const { data: schedules, isLoading } = useQuery({
    queryKey: ['schedules', systemId],
    queryFn: () => aiScheduleApi.list({ ai_system: systemId }),
    enabled: !!systemId,
  });

  const {
    data: eventTypes = [],
    error: eventTypesError,
    isFetching: isEventTypesLoading,
  } = useQuery({
    queryKey: ['ai-event-types', { includeAdmin: isAdmin }],
    queryFn: () => aiEventTypeApi.list({ includeAdmin: isAdmin }),
    enabled: formOpen && !!user,
  });
  const eventTypesErrorMessage = eventTypeAccessErrorMessage(eventTypesError);
  const accessibleEventTypes = useMemo(
    () => getAccessibleEventTypes(eventTypes, isAdmin),
    [eventTypes, isAdmin]
  );
  const selectedEventTypeIsAccessible =
    formData.type !== 'EVENT' ||
    accessibleEventTypes.some((eventType) => eventType.value === formData.event_type);
  const selectedEventType =
    accessibleEventTypes.find((eventType) => eventType.value === formData.event_type) || null;
  const selectedEventRequiresAlert =
    selectedEventType?.value === 'alerts.triggered' &&
    !formData.event_filters.payloadFields.alert_id;
  const { data: alerts = [], isFetching: isAlertsLoading } = useQuery({
    queryKey: ['alerts', 'event-schedule-picker'],
    queryFn: () => alertApi.list(),
    enabled: formOpen && selectedEventType?.value === 'alerts.triggered',
  });

  // Create schedule mutation
  const createMutation = useMutation({
    mutationFn: (data: AIScheduleCreate) => aiScheduleApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules', systemId] });
      queryClient.invalidateQueries({ queryKey: ['ai-system-validation', systemId] });
      handleCloseForm();
    },
  });

  // Update schedule mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<AIScheduleCreate> }) =>
      aiScheduleApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules', systemId] });
      queryClient.invalidateQueries({ queryKey: ['ai-system-validation', systemId] });
      handleCloseForm();
    },
  });

  // Delete schedule mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => aiScheduleApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules', systemId] });
      queryClient.invalidateQueries({ queryKey: ['ai-system-validation', systemId] });
      setDeleteConfirmOpen(false);
      setScheduleToDelete(null);
    },
  });

  // Activate/deactivate schedule mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      aiScheduleApi.update(id, { is_active }),
    onMutate: async ({ id, is_active }) => {
      await queryClient.cancelQueries({ queryKey: ['schedules', systemId] });
      const previous = queryClient.getQueryData(['schedules', systemId]);
      queryClient.setQueryData(['schedules', systemId], (old: any) => {
        if (!old?.results) return old;
        return {
          ...old,
          results: old.results.map((s: AISchedule) =>
            s.id === id ? { ...s, is_active } : s
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['schedules', systemId], context.previous);
      }
      setTogglingId(null);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules', systemId] });
      queryClient.invalidateQueries({ queryKey: ['ai-system-validation', systemId] });
      setTogglingId(null);
    },
  });

  const handleOpenForm = (schedule?: AISchedule) => {
    if (schedule) {
      setEditingSchedule(schedule);
      
      // Convert ISO datetime to datetime-local format (YYYY-MM-DDTHH:MM)
      const formatDatetimeLocal = (isoString: string | null): string => {
        if (!isoString) return '';
        // Parse the ISO string and format it as YYYY-MM-DDTHH:MM
        const date = new Date(isoString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };
      
      console.log('Loading schedule for editing:', schedule);
      console.log('interval_every:', schedule.interval_every);
      console.log('interval_period:', schedule.interval_period);
      const eventFilters = schedule.event_trigger?.filters || {};
      
      setFormData({
        type: schedule.type,
        start_at: formatDatetimeLocal(schedule.start_at),
        end_at: schedule.end_at ? formatDatetimeLocal(schedule.end_at) : null,
        interval_period: schedule.interval_period || null,
        interval_every: schedule.interval_every || null,
        excluded_days: schedule.excluded_days || null,
        excluded_time_ranges: schedule.excluded_time_ranges || null,
        event_type: schedule.event_trigger?.event_type || '',
        event_filters: eventFilterValueFromFilters(eventFilters),
        event_cooldown_seconds: schedule.event_trigger?.cooldown_seconds || 0,
        event_dedupe_window_seconds: schedule.event_trigger?.dedupe_window_seconds || 60,
        event_while_running_policy: schedule.event_trigger?.while_running_policy || 'drop',
        prevent_concurrent_executions: schedule.prevent_concurrent_executions || false,
        concurrency_policy: schedule.concurrency_policy || 'queue',
        expert_selection_mode: schedule.expert_selection_mode || 'ALL',
        expert_ids: schedule.expert_ids || [],
        is_active: schedule.is_active,
      });
    } else {
      setEditingSchedule(null);
      setFormData({
        type: 'ONE_TIME',
        start_at: '',
        end_at: null,
        interval_period: null,
        interval_every: null,
        excluded_days: null,
        excluded_time_ranges: null,
        event_type: '',
        event_filters: createEmptyEventFilterValue(),
        event_cooldown_seconds: 0,
        event_dedupe_window_seconds: 60,
        event_while_running_policy: 'drop',
        prevent_concurrent_executions: false,
        concurrency_policy: 'queue',
        expert_selection_mode: 'ALL',
        expert_ids: [],
        is_active: true,
      });
    }
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingSchedule(null);
  };

  const handleSubmit = () => {
    if (
      formData.type === 'EVENT' &&
      (eventTypesErrorMessage || !selectedEventTypeIsAccessible || selectedEventRequiresAlert)
    ) {
      return;
    }

    // Convert datetime-local format to ISO string with timezone
    const convertToISO = (datetimeLocal: string | null): string | null => {
      if (!datetimeLocal) return null;
      const localDate = new Date(datetimeLocal);
      return localDate.toISOString();
    };

    const scheduleData: AIScheduleCreate = {
      ai_system: systemId,
      type: formData.type,
      start_at: convertToISO(formData.start_at),
      end_at: convertToISO(formData.end_at),
      interval_period: formData.interval_period,
      interval_every: formData.interval_every,
      excluded_days: formData.excluded_days,
      excluded_time_ranges: formData.excluded_time_ranges,
      prevent_concurrent_executions: formData.prevent_concurrent_executions,
      concurrency_policy: formData.concurrency_policy,
      expert_selection_mode: formData.expert_selection_mode,
      expert_ids: formData.expert_ids,
      is_active: formData.is_active,
    };

    if (formData.type === 'EVENT') {
      scheduleData.event_config = {
        event_type: formData.event_type,
        filters: buildEventFilters(selectedEventType, formData.event_filters),
        conditions: [],
        cooldown_seconds: formData.event_cooldown_seconds || 0,
        dedupe_window_seconds: formData.event_dedupe_window_seconds || 60,
        while_running_policy: formData.event_while_running_policy,
        is_active: true,
      };
      scheduleData.interval_every = null;
      scheduleData.interval_period = null;
      scheduleData.excluded_days = null;
      scheduleData.excluded_time_ranges = null;
    }

    if (editingSchedule) {
      updateMutation.mutate({ id: editingSchedule.id, data: scheduleData });
    } else {
      createMutation.mutate(scheduleData);
    }
  };

  const handleDelete = (schedule: AISchedule) => {
    setScheduleToDelete(schedule);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (scheduleToDelete) {
      deleteMutation.mutate(scheduleToDelete.id);
    }
  };

  const handleToggleActive = (schedule: AISchedule) => {
    setTogglingId(schedule.id);
    toggleActiveMutation.mutate({
      id: schedule.id,
      is_active: !schedule.is_active,
    });
  };

  const formatRecurring = (schedule: AISchedule) => {
    if (schedule.type === 'ONE_TIME') {
      return 'One-time execution';
    }
    if (schedule.type === 'EVENT') {
      return `On ${schedule.event_trigger?.event_type_label || schedule.event_trigger?.event_type || 'event'}`;
    }
    if (schedule.interval_every && schedule.interval_period) {
      return `Every ${schedule.interval_every} ${schedule.interval_period.toLowerCase()}`;
    }
    return 'Recurring';
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const now = new Date().toISOString().slice(0, 16); // Format for datetime-local input

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <ScheduleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Execution Schedules
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm()}
          >
            Add Schedule
          </Button>
        </Box>

        {isLoading ? (
          <Typography>Loading schedules...</Typography>
        ) : schedules && schedules.results && schedules.results.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Experts</TableCell>
                  <TableCell>Concurrency</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.results.map((schedule) => (
                  <TableRow key={schedule.id}>
                    <TableCell>
                      <Chip
                        label={schedule.type}
                        size="small"
                        color={schedule.type === 'EVENT' ? 'secondary' : schedule.type === 'RECURRING' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{formatDateTime(schedule.start_at)}</TableCell>
                    <TableCell>{formatDateTime(schedule.end_at)}</TableCell>
                    <TableCell>{formatRecurring(schedule)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          schedule.expert_selection_mode === 'INCLUDE'
                            ? `Only ${schedule.expert_ids.length}`
                            : schedule.expert_selection_mode === 'EXCLUDE'
                              ? `Skip ${schedule.expert_ids.length}`
                              : 'All experts'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          schedule.prevent_concurrent_executions
                            ? schedule.concurrency_policy
                            : 'parallel'
                        }
                        size="small"
                        color={schedule.prevent_concurrent_executions ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={schedule.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={schedule.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(schedule)}
                        color={schedule.is_active ? 'warning' : 'success'}
                        disabled={togglingId === schedule.id}
                      >
                        {togglingId === schedule.id ? (
                          <CircularProgress size={18} />
                        ) : (
                          schedule.is_active ? <PauseIcon /> : <PlayIcon />
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenForm(schedule)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(schedule)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">
            No schedules configured. Click "Add Schedule" to create automated executions.
          </Alert>
        )}

        {/* Schedule Form Dialog */}
        <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingSchedule ? 'Edit Schedule' : 'Add Schedule'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  label="Schedule Type"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as ScheduleType })}
                >
                  <MenuItem value="ONE_TIME">One-Time Execution</MenuItem>
                  <MenuItem value="RECURRING">Recurring Execution</MenuItem>
                  <MenuItem value="EVENT">Event Trigger</MenuItem>
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  select
                  fullWidth
                  label="Experts to execute"
                  value={formData.expert_selection_mode}
                  onChange={(e) => {
                    const mode = e.target.value as 'ALL' | 'INCLUDE' | 'EXCLUDE';
                    setFormData({
                      ...formData,
                      expert_selection_mode: mode,
                      expert_ids: mode === 'ALL' ? [] : formData.expert_ids,
                    });
                  }}
                >
                  <MenuItem value="ALL">Run all active experts</MenuItem>
                  <MenuItem value="INCLUDE">Run only selected experts</MenuItem>
                  <MenuItem value="EXCLUDE">Run all except selected experts</MenuItem>
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 7 }}>
                <ExpertMultiAutocomplete
                  aiSystemId={systemId}
                  value={formData.expert_ids}
                  onChange={(expertIds) =>
                    setFormData({ ...formData, expert_ids: expertIds })
                  }
                  disabled={formData.expert_selection_mode === 'ALL'}
                  label={
                    formData.expert_selection_mode === 'EXCLUDE'
                      ? 'Experts to skip'
                      : 'Experts to run'
                  }
                  helperText={
                    formData.expert_selection_mode === 'ALL'
                      ? 'Every active expert in the system will run.'
                      : 'The selection is exact; dependencies outside it are not run automatically.'
                  }
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={formData.type === 'EVENT' ? 'Start Time (Optional)' : 'Start Time'}
                  type="datetime-local"
                  value={formData.start_at}
                  onChange={(e) => setFormData({ ...formData, start_at: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: now }}
                />
              </Grid>

              {(formData.type === 'RECURRING' || formData.type === 'EVENT') && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="End Time (Optional)"
                    type="datetime-local"
                    value={formData.end_at || ''}
                    onChange={(e) => setFormData({ ...formData, end_at: e.target.value || null })}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: formData.start_at || now }}
                  />
                </Grid>
              )}

              {formData.type === 'EVENT' && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <EventTypeAutocomplete
                      eventTypes={eventTypes}
                      value={formData.event_type}
                      onChange={(eventType) =>
                        setFormData({
                          ...formData,
                          event_type: eventType,
                          event_filters: createEmptyEventFilterValue(),
                        })
                      }
                      loading={isEventTypesLoading}
                      accessError={eventTypesErrorMessage}
                      disabled={createMutation.isPending || updateMutation.isPending}
                      required
                      isAdmin={isAdmin}
                    />
                    {formData.event_type && !selectedEventTypeIsAccessible && !isEventTypesLoading && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Selected event type is unavailable to this account. Choose an accessible event type.
                      </Alert>
                    )}
                  </Grid>

                  <EventFilterFields
                    eventType={selectedEventType}
                    value={formData.event_filters}
                    onChange={(eventFilters) =>
                      setFormData({ ...formData, event_filters: eventFilters })
                    }
                    disabled={createMutation.isPending || updateMutation.isPending}
                    alerts={alerts}
                    alertsLoading={isAlertsLoading}
                  />
                  {selectedEventRequiresAlert && (
                    <Grid size={{ xs: 12 }}>
                      <Alert severity="warning">
                        Select the alert that should trigger this AI system.
                      </Alert>
                    </Grid>
                  )}

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Cooldown Seconds"
                      value={formData.event_cooldown_seconds}
                      onChange={(e) => setFormData({ ...formData, event_cooldown_seconds: parseInt(e.target.value) || 0 })}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Dedupe Window Seconds"
                      value={formData.event_dedupe_window_seconds}
                      onChange={(e) => setFormData({ ...formData, event_dedupe_window_seconds: parseInt(e.target.value) || 60 })}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      select
                      fullWidth
                      label="While Running"
                      value={formData.event_while_running_policy}
                      onChange={(e) => setFormData({ ...formData, event_while_running_policy: e.target.value as 'drop' | 'queue' })}
                    >
                      <MenuItem value="drop">Drop</MenuItem>
                      <MenuItem value="queue">Queue</MenuItem>
                    </TextField>
                  </Grid>
                </>
              )}

              {formData.type === 'RECURRING' && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Repeat Every"
                      value={formData.interval_every || ''}
                      onChange={(e) => setFormData({ ...formData, interval_every: parseInt(e.target.value) || null })}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      select
                      fullWidth
                      label="Time Unit"
                      value={formData.interval_period || ''}
                      onChange={(e) => setFormData({ ...formData, interval_period: e.target.value as RecurringUnit })}
                      SelectProps={{
                        displayEmpty: true,
                      }}
                    >
                      <MenuItem value="">Select Time Unit</MenuItem>
                      <MenuItem value="seconds">Seconds</MenuItem>
                      <MenuItem value="minutes">Minutes</MenuItem>
                      <MenuItem value="hours">Hours</MenuItem>
                      <MenuItem value="days">Days</MenuItem>
                      <MenuItem value="weeks">Weeks</MenuItem>
                    </TextField>
                  </Grid>
                </>
              )}

              {formData.type === 'RECURRING' && (
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth>
                    <TextField
                      select
                      label="Exclude Days (Optional)"
                      SelectProps={{
                        multiple: true,
                        value: formData.excluded_days || [],
                        onChange: (e) => setFormData({ 
                          ...formData, 
                          excluded_days: (e.target.value as WeekDay[]).length > 0 ? (e.target.value as WeekDay[]) : null 
                        }),
                        renderValue: (selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as WeekDay[]).map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        ),
                      }}
                    >
                      <MenuItem value="MONDAY">Monday</MenuItem>
                      <MenuItem value="TUESDAY">Tuesday</MenuItem>
                      <MenuItem value="WEDNESDAY">Wednesday</MenuItem>
                      <MenuItem value="THURSDAY">Thursday</MenuItem>
                      <MenuItem value="FRIDAY">Friday</MenuItem>
                      <MenuItem value="SATURDAY">Saturday</MenuItem>
                      <MenuItem value="SUNDAY">Sunday</MenuItem>
                    </TextField>
                  </FormControl>
                </Grid>
              )}

              {formData.type === 'RECURRING' && (
                <Grid size={{ xs: 12 }}>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Exclude Time Ranges (Optional)
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const newRanges = [...(formData.excluded_time_ranges || []), { start: '00:00', end: '00:00' }];
                          setFormData({ ...formData, excluded_time_ranges: newRanges });
                        }}
                      >
                        Add Time Range
                      </Button>
                    </Box>
                    
                    {formData.excluded_time_ranges && formData.excluded_time_ranges.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {formData.excluded_time_ranges.map((range, index) => (
                          <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              size="small"
                              type="time"
                              label="Start Time"
                              value={range.start}
                              onChange={(e) => {
                                const newRanges = [...formData.excluded_time_ranges!];
                                newRanges[index] = { ...newRanges[index], start: e.target.value };
                                setFormData({ ...formData, excluded_time_ranges: newRanges });
                              }}
                              InputLabelProps={{ shrink: true }}
                            />
                            <Typography variant="body2" color="text.secondary">to</Typography>
                            <TextField
                              size="small"
                              type="time"
                              label="End Time"
                              value={range.end}
                              onChange={(e) => {
                                const newRanges = [...formData.excluded_time_ranges!];
                                newRanges[index] = { ...newRanges[index], end: e.target.value };
                                setFormData({ ...formData, excluded_time_ranges: newRanges });
                              }}
                              InputLabelProps={{ shrink: true }}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                const newRanges = formData.excluded_time_ranges!.filter((_, i) => i !== index);
                                setFormData({ ...formData, excluded_time_ranges: newRanges.length > 0 ? newRanges : null });
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    {(!formData.excluded_time_ranges || formData.excluded_time_ranges.length === 0) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        No time ranges excluded. Click "Add Time Range" to exclude specific time periods (e.g., overnight 22:00-06:00).
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                  label="Active (start executing immediately)"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.prevent_concurrent_executions}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          prevent_concurrent_executions: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Single active execution"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Overlap Policy"
                  value={formData.concurrency_policy}
                  disabled={!formData.prevent_concurrent_executions}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      concurrency_policy: e.target.value as 'queue' | 'drop',
                    })
                  }
                >
                  <MenuItem value="queue">Queue</MenuItem>
                  <MenuItem value="drop">Drop</MenuItem>
                </TextField>
              </Grid>

              {formData.type === 'RECURRING' && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Recurring Schedule:</strong> The system will execute every{' '}
                      {formData.interval_every || 'X'} {formData.interval_period?.toLowerCase() || 'unit(s)'}
                      {formData.end_at && ` until ${formatDateTime(formData.end_at)}`}.
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForm}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                (formData.type !== 'EVENT' && !formData.start_at) ||
                (formData.type === 'RECURRING' && (!formData.interval_period || !formData.interval_every)) ||
                (formData.type === 'EVENT' && (!selectedEventTypeIsAccessible || selectedEventRequiresAlert)) ||
                (formData.expert_selection_mode !== 'ALL' && formData.expert_ids.length === 0) ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {editingSchedule ? 'Update' : 'Create'} Schedule
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this schedule? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};
